import type { HistoricalConcert } from "../types";
import { formatHistoricalDateLine } from "../utils/historicalDate";
import { focusHistoricalConcertOnMap } from "../state/store";
import { hasMapLocation } from "../utils/geo";
import { ProgramList } from "./ProgramList";
import { EvidenceBlock } from "./EvidenceBlock";

interface HistoricalConcertCardProps {
  concert: HistoricalConcert;
}

/** Archive-mode's concert card. Deliberately the SAME shell as <ConcertCard> (same class
 *  names for date/title/venue/program rows, same <ProgramList>) so switching eras reads as
 *  a lens change, not a different app — the one addition is <EvidenceBlock>, which replaces
 *  the modern card's freshness disclosure with this era's honest equivalent: not "how
 *  recently did we check", but "how do we know this happened at all". */
export function HistoricalConcertCard({ concert }: HistoricalConcertCardProps) {
  const hasProgram = concert.program.length > 0;
  // With a program the card leads with it; a titleless programless record falls back to
  // its first billed line so the row is never blank.
  const title = concert.title || (hasProgram ? null : concert.program[0]?.work_display || concert.program[0]?.work || "Untitled");
  const cityLine = concert.place_raw && concert.place_raw !== concert.city_name
    ? `${concert.city_name} (${concert.place_raw})`
    : concert.city_name;

  function handleShowOnMap() {
    focusHistoricalConcertOnMap(concert);
    requestAnimationFrame(() => {
      document.querySelector(".map-pane")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  /* Confidence badging is deliberately quiet: the source line below (EvidenceBlock)
     already says exactly where a non-live record comes from, so a "probable" badge on
     every Wikidata/memoir row was pure noise. Only the opt-in "possible" tier keeps its
     badge — the Uncertain-events toggle explicitly promises one. */
  const badge = concert.status === "possible" && (
    <span
      class="confidence-badge"
      title="From a memoir passage that never pins this event to an exact day or place — shown because “Uncertain events” is enabled."
    >
      {concert.status}
    </span>
  );
  const titleBlock = (title || concert.org_name || badge) ? (
    <div class={hasProgram ? "concert-billing" : undefined}>
      {hasProgram && title && <span class="concert-billing-label">Billed as</span>}
      {(title || badge) && (
        <h3 class="concert-title">
          {title}
          {badge}
        </h3>
      )}
      {concert.org_name && <div class="concert-org">{concert.org_name}</div>}
    </div>
  ) : null;

  return (
    <article class={`concert-card historical-card${hasProgram ? " has-program" : ""}`} role="listitem">
      <div class="concert-card-main">
        <div class="concert-body">
          <div class="concert-date" aria-label="Date">
            <span>{formatHistoricalDateLine(concert)}</span>
            <span class="concert-kind concert-kind-archive" title="Reconstructed from an archive, memoir or database — see the source line">Archive</span>
          </div>
          {!hasProgram && titleBlock}
          <div class="concert-venue">
            <svg class="concert-venue-icon" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
              <path
                fill="currentColor"
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"
              />
            </svg>
            {concert.venue_name && <span>{concert.venue_name} · </span>}
            <span class="concert-city">{cityLine}</span>
          </div>

          <ProgramList program={concert.program} />
          {hasProgram && titleBlock}

          {concert.performers && concert.performers.length > 0 && (
            <div class="concert-performers">
              {concert.performers.map((p, i) => (
                <span key={i} class="performer">
                  {p.name}
                  {p.role && <span class="performer-role">, {p.role}</span>}
                  {i < concert.performers!.length - 1 && " · "}
                </span>
              ))}
            </div>
          )}

          <EvidenceBlock
            source={concert.source}
            witness={concert.witness}
            quote={concert.quote}
            excerpt={concert.evidence_excerpt}
            locator={concert.locator}
          />
        </div>
      </div>

      {hasMapLocation(concert) && (
        <div class="concert-actions">
          <button class="concert-map-link" onClick={handleShowOnMap} type="button">
            Show on map
          </button>
        </div>
      )}
    </article>
  );
}
